import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
// The subpath, not the root barrel: it resolves to this dataset alone instead
// of relying on the bundler to tree-shake the payment nomenclatures away. The
// table is small (~27 KB) and read synchronously below, so it is imported
// eagerly — unlike the procedure indexes, which go through the lazy loaders.
import { countries as countriesJson } from "@kenettotech/scribo-shared-data/countries";

export interface Country {
  code: string;
  emoji: string;
  unicode: string;
  /** International dial code, including the leading "+". */
  dialCode: string;
  /** English name. */
  name: string;
  /** Localised (pt-PT) name — what we show. */
  label: string;
}

const countries = countriesJson as Record<string, Country>;

/** ISO-2 code used as the default across the app's forms. */
export const DEFAULT_COUNTRY_CODE = "PT";

export const getCountry = (code: string): Country | undefined =>
  countries[code];

/** Countries sorted by their localised name, for select inputs. */
export const countryOptions = Object.values(countries)
  .map(country => ({ label: country.label, value: country.code }))
  .sort((a, b) => a.label.localeCompare(b.label, "pt"));

/**
 * Builds the phone string the payments API expects: dial code, a space, then
 * the local number — e.g. "+351 912345678".
 */
export const formatPhoneNumber = (countryCode: string, number: string) =>
  `${getCountry(countryCode)?.dialCode ?? ""} ${number}`.trim();

/**
 * Validates a number against the rules of its country, mirroring the mobile's
 * `validatePhoneNumber`. A plain length check accepts numbers with the right
 * number of digits but an impossible prefix for the country.
 *
 * Note this only decides what is accepted — the value sent to the API keeps the
 * `${dialCode} ${number}` format built by formatPhoneNumber.
 */
export const isValidLocalPhoneNumber = (
  countryCode: string,
  number: string
): boolean => {
  const country = getCountry(countryCode);
  if (!country) return false;
  return isValidPhoneNumber(
    `${country.dialCode} ${number}`,
    country.code as CountryCode
  );
};
